// Same cache shape as the main mail list (`mails.list(requestId)`) so the
// shared useMailActions mutates the assistant's list directly.
import Email from "@/domain/entities/emails/Email";
import { isSameCopy } from "@/lib/mailIdentity";
import type { InfiniteData } from "@tanstack/react-query";

export type AssistantMailsPage = {
    data: Email[];
    currentPage: number;
    nextPage: number | null;
};

export type AssistantMailsCache = InfiniteData<AssistantMailsPage>;

/**
 * The list of the mails an answer cites (step-based contract): its own cache key, apart from the mails
 * of the action the answer may propose (keyed by the answer's `userRequestId`).
 */
export const answerMailsListId = (userRequestId: string): string =>
    `${userRequestId}:answer`;

/** The mails of a list, all its pages together. */
export const assistantMails = (
    cache: AssistantMailsCache | undefined,
): Email[] => cache?.pages.flatMap((page) => page.data) ?? [];

export const createAssistantMailsCache = (
    emails: Email[],
): AssistantMailsCache => ({
    pages: [{ data: emails, currentPage: 0, nextPage: null }],
    pageParams: [0],
});

export const appendAssistantMails = (
    cache: AssistantMailsCache | undefined,
    emails: Email[],
): AssistantMailsCache => {
    if (!cache) return createAssistantMailsCache(emails);

    return {
        ...cache,
        pages: cache.pages.map((page, index) =>
            index === cache.pages.length - 1
                ? { ...page, data: [...page.data, ...emails] }
                : page,
        ),
    };
};

export const updateAssistantMail = (
    cache: AssistantMailsCache | undefined,
    updatedMail: Email,
): AssistantMailsCache | undefined => {
    if (!cache) return cache;

    return {
        ...cache,
        pages: cache.pages.map((page) => ({
            ...page,
            data: page.data.map((mail) =>
                isSameCopy(mail, updatedMail) ? updatedMail : mail,
            ),
        })),
    };
};

export const removeAssistantMail = (
    cache: AssistantMailsCache | undefined,
    uniqueMailvistaId: string,
): AssistantMailsCache | undefined => {
    if (!cache) return cache;

    return {
        ...cache,
        pages: cache.pages.map((page) => ({
            ...page,
            data: page.data.filter(
                (mail) => mail.uniqueMailvistaId !== uniqueMailvistaId,
            ),
        })),
    };
};
